import { useRef } from 'react'

interface PassHtmlEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

const PassHtmlEditor = ({ value, onChange, placeholder }: PassHtmlEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const insertTag = (openTag: string, closeTag: string) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)

    if (selectedText) {
      const newValue = `${value.substring(0, start)}${openTag}${selectedText}${closeTag}${value.substring(end)}`
      onChange(newValue)

      // Reset focus and cursor position
      setTimeout(() => {
        textarea.focus()
        const newPosition = start + openTag.length + selectedText.length + closeTag.length
        textarea.setSelectionRange(newPosition, newPosition)
      }, 0)
    }
  }

  const insertBold = () => insertTag('<b>', '</b>')
  const insertStrong = () => insertTag('<strong>', '</strong>')
  const insertItalic = () => insertTag('<i>', '</i>')
  const insertEmphasis = () => insertTag('<em>', '</em>')

  const insertLink = () => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)

    const url = prompt('Enter URL:')
    if (url) {
      const linkText = selectedText || 'link text'
      const newValue = `${value.substring(0, start)}<a href="${url}">${linkText}</a>${value.substring(end)}`
      onChange(newValue)

      // Reset focus and cursor position
      setTimeout(() => {
        textarea.focus()
        const newPosition = start + `<a href="${url}">${linkText}</a>`.length
        textarea.setSelectionRange(newPosition, newPosition)
      }, 0)
    }
  }

  return (
    <div style={{ margin: '20px 0' }}>
      <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
        <strong>Pass Content Editor:</strong> Select text and use the buttons below. Supports bold
        (&lt;b&gt;/&lt;strong&gt;), italic (&lt;i&gt;/&lt;em&gt;), and links.
      </div>

      {/* Toolbar */}
      <div
        style={{
          marginBottom: '10px',
          padding: '8px',
          border: '1px solid #d1d5db',
          borderRadius: '4px 4px 0 0',
          backgroundColor: '#f9fafb',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={insertBold}
          style={{
            padding: '6px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
          title="Bold with <b> tag"
        >
          <b>B</b>
        </button>

        <button
          type="button"
          onClick={insertStrong}
          style={{
            padding: '6px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '12px',
          }}
          title="Strong emphasis with <strong> tag"
        >
          <strong>STR</strong>
        </button>

        <button
          type="button"
          onClick={insertItalic}
          style={{
            padding: '6px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontStyle: 'italic',
          }}
          title="Italic with <i> tag"
        >
          <i>I</i>
        </button>

        <button
          type="button"
          onClick={insertEmphasis}
          style={{
            padding: '6px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontStyle: 'italic',
            fontSize: '12px',
          }}
          title="Emphasis with <em> tag"
        >
          <em>EM</em>
        </button>

        <button
          type="button"
          onClick={insertLink}
          style={{
            padding: '6px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
          }}
          title="Add link to selected text"
        >
          🔗
        </button>
      </div>

      {/* Text Editor */}
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Enter content for the back of the pass...'}
        style={{
          width: '100%',
          minHeight: '200px',
          padding: '12px',
          border: '1px solid #d1d5db',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          fontSize: '14px',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
          resize: 'vertical',
          outline: 'none',
        }}
        onFocus={e => {
          e.target.style.border = '1px solid #3b82f6'
          e.target.style.borderTop = 'none'
        }}
        onBlur={e => {
          e.target.style.border = '1px solid #d1d5db'
          e.target.style.borderTop = 'none'
        }}
      />
    </div>
  )
}

export default PassHtmlEditor
